import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { TDetailsSeparateTextData } from '../../types/data';
import { TDetailsSeparateTextOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import styles from './styles/detailsSeparateText.module.css';

export const DetailsSeparateText: React.FC<TWidgetProps<TDetailsSeparateTextData, TDetailsSeparateTextOption>> = ({ widget }) => {
    const { triggerActionsByType } = useTriggerAction(widget);
    const { isDisabled, isHidden } = useBehavior(widget);

    const { rightText, leftText, messages } = widget.data ?? ({} as TDetailsSeparateTextData);
    const { iconName, rightTextAlignment, rowSize, margin } = widget.options ?? {};

    const onClick = () => {
        if (!isDisabled && iconName) {
            triggerActionsByType(ActionActionTypeEnum.onClick, {
                playerActionOptions: {
                    textForCopyToClipboard: rightText,
                    toastMessage: messages?.copiedText,
                },
            }).then();
        }
    };

    const isSmall = rowSize === 'small';
    const isEnd = rightTextAlignment === 'end';
    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];

    return (
        <CoreLayoutView
            uid={widget.uid}
            isHide={isHidden}
            isDisabled={isDisabled}
            parentStyleNames={parentStyleNames}
            styleName="detailsSeparateText"
            styleProps={{
                orientation: ORIENTATION_ENUM.HORIZONTAL,
                lineX: 'space_between',
                lineY: 'start',
                padding: isSmall ? { top: 0, right: 24, bottom: 0, left: 24 } : { top: 16, right: 24, bottom: 16, left: 24 },
                gap: 24,
                margin: margin,
                width: '100%',
                hovered: {
                    backgroundColor: iconName ? 'var(--color-grey_16_lc)' : undefined,
                },
                pressed: {
                    backgroundColor: iconName ? 'var(--color-grey_8_lc)' : undefined,
                },
            }}
        >
            <CoreTextView
                text={leftText}
                parentStyleNames={parentStyleNames}
                isDisabled={isDisabled}
                styleName="detailsSeparateTextLeft"
                style={{ flex: 1 }}
                styleProps={{
                    ...(isSmall ? TextStyle.secondary : TextStyle.main),
                    main: 'var(--color-grey_72_lc)',
                    disabled: {
                        main: 'var(--color-grey_40_lc)',
                    },
                }}
            />
            <div
                className={clsx(styles.rightSection, iconName && styles.clickable, isEnd && styles.rightSectionEnd)}
                onClick={() => {
                    if (!isDisabled && iconName) {
                        onClick();
                    }
                }}
            >
                <CoreTextView
                    text={rightText}
                    parentStyleNames={parentStyleNames}
                    isDisabled={isDisabled}
                    styleName="detailsSeparateTextRight"
                    styleProps={{
                        ...(isSmall ? TextStyle.secondary : TextStyle.main),
                        textAlign: isEnd ? 'right' : 'left',
                        main: 'var(--color-black)',
                        disabled: {
                            main: 'var(--color-grey_40_lc)',
                        },
                    }}
                />
                {iconName && (
                    <VisualView
                        iconName={iconName}
                        defaultIconName={AssetsSvgMap.ic_x}
                        size="xxSmall"
                        isDisabled={isDisabled}
                        iconVariant="simple"
                        iconHierarchy="primary"
                        parentStyleNames={[...parentStyleNames, 'detailsSeparateTextIcon']}
                        styleName="detailsSeparateTextIcon"
                    />
                )}
            </div>
        </CoreLayoutView>
    );
};
